import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
    PowerPointPassportHeader,
    PowerPointPassportID,
    PowerPointPassport,
    PowerPointLoadState,
    PowerPointEmbedUrlRequest,
    PowerPointPassportDefault,
    PowerPointPassportFormState,
    PowerPointPassportAuthCode,
    PowerPointCodeChallenge,
} from "userful-chronos-app-common-js/dist/models/integration/powerpoint";
import {
    requestListPassports,
    requestAddPassport,
    requestUpdatePassport,
    requestRemovePassport,
    requestUpdatePassportWithOAuth,
    requestBaseURL,
    requestPreviewURL,
    requestCodeChallenge,
    requestAccessToken,
} from "./msgs/powerpointMsgSender";

const initialState: {
    powerpointPassports: PowerPointPassport[];
    loadState: PowerPointLoadState;
    passport: PowerPointPassport;
    copyPassport: PowerPointPassport;
    formType: PowerPointPassportFormState;
    openForm: boolean;
    authCode: string;
    codeChallenge: string;
    accessTokenGraph: string;
    accessTokenPowerPoint: string;
    baseURL: string;
} = {
    powerpointPassports: [],
    loadState: "RENDER",
    passport: PowerPointPassportDefault(),
    copyPassport: PowerPointPassportDefault(),
    formType: "Create",
    openForm: false,
    authCode: "",
    codeChallenge: "",
    accessTokenGraph: "",
    accessTokenPowerPoint: "",
    baseURL: "",
};

export const powerpointSlice = createSlice({
    name: "powerpointSlice",
    initialState,
    reducers: {
        setPassports: (state, action: PayloadAction<PowerPointPassport[]>) => {
            state.powerpointPassports = action.payload;
        },
        addOrUpdatePassport: (state, action: PayloadAction<PowerPointPassport>) => {
            const foundIndex = state.powerpointPassports.findIndex((item) => item.id.value === action.payload.id.value);
            if (foundIndex < 0) {
                state.powerpointPassports = [...state.powerpointPassports, { ...action.payload }];
            } else {
                state.powerpointPassports = [
                    ...state.powerpointPassports.slice(0, foundIndex),
                    { ...action.payload },
                    ...state.powerpointPassports.slice(foundIndex + 1),
                ];
            }
        },
        removePassport: (state, action: PayloadAction<PowerPointPassportID>) => {
            state.powerpointPassports = state.powerpointPassports.filter(
                (item) => item.id.value !== action.payload.value,
            );
        },
        getPassportsFromServer: (state, action: PayloadAction<{}>) => {
            requestListPassports();
        },
        addPassportToServer: (state, action: PayloadAction<PowerPointPassportHeader>) => {
            requestAddPassport(action.payload);
        },
        updatePassportToServerWithOAuth: (state, action: PayloadAction<PowerPointPassportHeader>) => {
            requestUpdatePassportWithOAuth(action.payload);
        },
        updatePassportToServer: (state, action: PayloadAction<PowerPointPassportHeader>) => {
            requestUpdatePassport(action.payload);
        },
        removePassportToServer: (state, action: PayloadAction<PowerPointPassportID>) => {
            requestRemovePassport(action.payload);
        },
        changeLoadState: (state, action: PayloadAction<PowerPointLoadState>) => {
            state.loadState = action.payload;
        },
        setPassport: (state, action: PayloadAction<PowerPointPassport>) => {
            state.passport = action.payload;
        },
        setCopyPassport: (state, action: PayloadAction<PowerPointPassport>) => {
            state.copyPassport = action.payload;
        },
        setFormType: (state, action: PayloadAction<PowerPointPassportFormState>) => {
            state.formType = action.payload;
        },
        setOpenForm: (state, action: PayloadAction<boolean>) => {
            state.openForm = action.payload;
        },
        setAuthCode: (state, action: PayloadAction<string>) => {
            state.authCode = action.payload;
        },
        setCodeChallenge: (state, action: PayloadAction<string>) => {
            state.codeChallenge = action.payload;
        },
        requestCodeChallenge: (state, action: PayloadAction<PowerPointPassportID>) => {
            requestCodeChallenge(action.payload);
        },
        requestBaseURL: (state, action: PayloadAction<PowerPointPassportID>) => {
            requestBaseURL(action.payload);
        },
        requestAccessToken: (state, action: PayloadAction<PowerPointPassportID>) => {
            requestAccessToken(action.payload);
        },
        setAccessTokenGraph: (state, action: PayloadAction<string>) => {
            state.accessTokenGraph = action.payload;
        },
        setAccessTokenPowerPoint: (state, action: PayloadAction<string>) => {
            state.accessTokenPowerPoint = action.payload;
        },
        setBaseURL: (state, action: PayloadAction<string>) => {
            state.baseURL = action.payload;
        },
    },
});

export const powerpointActions = powerpointSlice.actions;

export default powerpointSlice.reducer;
